import { useEffect, useState, type FormEvent } from 'react';
import { Plus, Save, Send, Trash2 } from 'lucide-react';
import type { AlertType, StreamSummary } from '@shared/protocol';
import { api } from '../lib/api';
import { useDocumentTitle } from '../lib/branding';
import { Notice } from '../components/Status';

interface Settings {
  webhookUrl: string | null;
  webhookFormat: 'slack' | 'teams' | 'generic';
  alertSound: boolean;
  llmSummary: boolean;
}

interface AlertRule {
  id?: number;
  type: AlertType;
  streamId: number | null;
  params: Record<string, number>;
  enabled: boolean;
}

const RULES: Record<AlertType, { label: string; params: { key: string; label: string; unit: string; def: number }[] }> = {
  true_peak: { label: 'True peak over limit', params: [{ key: 'thresholdDbtp', label: 'Limit', unit: 'dBTP', def: -1 }] },
  short_term: {
    label: 'Short-term loudness out of range',
    params: [
      { key: 'minLufs', label: 'Below', unit: 'LUFS', def: -40 },
      { key: 'maxLufs', label: 'Above', unit: 'LUFS', def: -10 },
      { key: 'seconds', label: 'For', unit: 's', def: 10 },
    ],
  },
  silence: {
    label: 'Silence',
    params: [
      { key: 'thresholdDb', label: 'Below', unit: 'dBFS', def: -60 },
      { key: 'seconds', label: 'For', unit: 's', def: 10 },
    ],
  },
  phase: {
    label: 'Phase correlation low',
    params: [
      { key: 'threshold', label: 'Below', unit: '', def: -0.3 },
      { key: 'seconds', label: 'For', unit: 's', def: 5 },
    ],
  },
  srt_loss: {
    label: 'SRT packet loss',
    params: [
      { key: 'percent', label: 'Above', unit: '%', def: 1 },
      { key: 'seconds', label: 'For', unit: 's', def: 5 },
    ],
  },
  stalled: { label: 'Stream stalled or lost', params: [{ key: 'seconds', label: 'For', unit: 's', def: 10 }] },
  new_fail: { label: 'Any new FAIL finding', params: [] },
};

export function SettingsPage() {
  useDocumentTitle('Settings');
  const [settings, setSettings] = useState<Settings | null>(null);
  const [llmAvailable, setLlmAvailable] = useState(false);
  const [retention, setRetention] = useState(30);
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [streams, setStreams] = useState<StreamSummary[]>([]);
  const [msg, setMsg] = useState<{ level: 'pass' | 'fail'; text: string } | null>(null);

  useEffect(() => {
    api<{ settings: Settings; llmAvailable: boolean; retentionDays: number }>('GET', '/api/settings').then((r) => {
      setSettings(r.settings);
      setLlmAvailable(r.llmAvailable);
      setRetention(r.retentionDays);
    });
    api<{ rules: AlertRule[] }>('GET', '/api/alert-rules').then((r) => setRules(r.rules));
    api<{ streams: StreamSummary[] }>('GET', '/api/streams').then((r) => setStreams(r.streams));
  }, []);

  const save = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!settings) return;
    setMsg(null);
    try {
      const r = await api<{ settings: Settings }>('PUT', '/api/settings', { ...settings, webhookUrl: settings.webhookUrl || null });
      setSettings(r.settings);
      setMsg({ level: 'pass', text: 'Settings saved.' });
    } catch (err) {
      setMsg({ level: 'fail', text: err instanceof Error ? err.message : 'Could not save.' });
    }
  };

  const test = async () => {
    setMsg(null);
    try {
      await api('POST', '/api/settings/test-webhook');
      setMsg({ level: 'pass', text: 'Test alert sent.' });
    } catch (err) {
      setMsg({ level: 'fail', text: err instanceof Error ? err.message : 'The test failed.' });
    }
  };

  const saveRule = async (i: number) => {
    setMsg(null);
    try {
      const r = await api<{ rule: AlertRule }>('POST', '/api/alert-rules', rules[i]);
      setRules((rs) => rs.map((x, j) => (j === i ? r.rule : x)));
      setMsg({ level: 'pass', text: `Alert rule "${RULES[r.rule.type].label}" saved.` });
    } catch (err) {
      setMsg({ level: 'fail', text: err instanceof Error ? err.message : 'Could not save the rule.' });
    }
  };

  const deleteRule = async (i: number) => {
    const r = rules[i];
    if (r.id) await api('DELETE', `/api/alert-rules/${r.id}`).catch(() => {});
    setRules((rs) => rs.filter((_, j) => j !== i));
  };

  const updateRule = (i: number, patch: Partial<AlertRule>) => setRules((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  if (!settings) return <div className="page narrow muted">Loading</div>;

  return (
    <div className="page narrow">
      <div className="page-header">
        <div>
          <h1>Settings</h1>
          <p className="muted">Alerts, notifications and summaries for all streams. Measurement logs are kept for {retention} days (MEASUREMENT_RETENTION_DAYS).</p>
        </div>
      </div>
      {msg && (
        <div style={{ marginBottom: 16 }}>
          <Notice level={msg.level}>{msg.text}</Notice>
        </div>
      )}
      <form className="card" onSubmit={save} style={{ marginBottom: 24 }}>
        <header className="card-header">
          <h2>Notifications</h2>
        </header>
        <div className="card-body">
          <div className="form-grid">
            <div className="field field-wide">
              <label htmlFor="wh-url">Webhook URL</label>
              <input id="wh-url" className="input mono" type="url" value={settings.webhookUrl ?? ''} onChange={(e) => setSettings({ ...settings, webhookUrl: e.target.value })} placeholder="https://hooks.slack.com/services/..." />
              <span className="hint">Every alert is posted here. For Teams, create a Workflows webhook ("When a Teams webhook request is received").</span>
            </div>
            <div className="field">
              <label htmlFor="wh-fmt">Format</label>
              <select id="wh-fmt" className="input" value={settings.webhookFormat} onChange={(e) => setSettings({ ...settings, webhookFormat: e.target.value as Settings['webhookFormat'] })}>
                <option value="slack">Slack</option>
                <option value="teams">Microsoft Teams</option>
                <option value="generic">Generic JSON</option>
              </select>
            </div>
          </div>
          <label className="checkbox">
            <input type="checkbox" checked={settings.alertSound} onChange={(e) => setSettings({ ...settings, alertSound: e.target.checked })} /> Play a sound in the browser when an alert is raised
          </label>
          <label className="checkbox">
            <input type="checkbox" checked={settings.llmSummary} disabled={!llmAvailable} onChange={(e) => setSettings({ ...settings, llmSummary: e.target.checked })} /> Natural-language summary by Claude (sends the structured findings to the Anthropic API)
          </label>
          <p className="hint">
            {llmAvailable
              ? 'The rules engine remains the source of truth; the generated text may only restate its findings and is shown next to them.'
              : 'Set ANTHROPIC_API_KEY on the server to enable this option.'}
          </p>
        </div>
        <footer className="modal-footer">
          <button type="button" className="btn" onClick={() => void test()} disabled={!settings.webhookUrl}>
            <Send size={14} aria-hidden /> Send test alert
          </button>
          <button type="submit" className="btn btn-primary">
            <Save size={14} aria-hidden /> Save
          </button>
        </footer>
      </form>

      <section className="card">
        <header className="card-header">
          <h2>Alert rules</h2>
        </header>
        <div className="card-body">
          <p className="muted small">Each rule raises one alert when its condition starts (then waits for it to clear). Scope a rule to one stream or leave it for all streams.</p>
          <div className="table-scroll">
            <table className="table table-compact">
              <thead>
                <tr>
                  <th>On</th>
                  <th>Rule</th>
                  <th>Settings</th>
                  <th>Streams</th>
                  <th className="actions">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rules.map((r, i) => (
                  <tr key={r.id ?? `new-${i}`}>
                    <td>
                      <input type="checkbox" aria-label="Enabled" checked={r.enabled} onChange={(e) => updateRule(i, { enabled: e.target.checked })} />
                    </td>
                    <td>
                      {r.id ? (
                        RULES[r.type].label
                      ) : (
                        <select
                          className="input input-inline"
                          value={r.type}
                          onChange={(e) => {
                            const type = e.target.value as AlertType;
                            updateRule(i, { type, params: Object.fromEntries(RULES[type].params.map((p) => [p.key, p.def])) });
                          }}
                        >
                          {Object.entries(RULES).map(([k, v]) => (
                            <option key={k} value={k}>
                              {v.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="rule-params">
                      {RULES[r.type].params.map((p) => (
                        <label key={p.key} className="rule-param">
                          <span className="muted small">{p.label}</span>
                          <input
                            className="input input-inline input-narrow mono"
                            type="number"
                            step="any"
                            value={r.params[p.key] ?? p.def}
                            onChange={(e) => updateRule(i, { params: { ...r.params, [p.key]: Number(e.target.value) } })}
                          />
                          <span className="muted small">{p.unit}</span>
                        </label>
                      ))}
                      {RULES[r.type].params.length === 0 && <span className="muted small">none</span>}
                    </td>
                    <td>
                      <select className="input input-inline" value={r.streamId ?? ''} onChange={(e) => updateRule(i, { streamId: e.target.value ? Number(e.target.value) : null })}>
                        <option value="">All streams</option>
                        {streams.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="actions nowrap">
                      <button type="button" className="btn btn-sm" onClick={() => void saveRule(i)}>
                        <Save size={14} aria-hidden /> Save
                      </button>
                      <button type="button" className="btn btn-sm btn-ghost btn-icon btn-danger" aria-label="Delete rule" onClick={() => void deleteRule(i)}>
                        <Trash2 size={14} aria-hidden />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className="btn btn-sm" style={{ marginTop: 12 }} onClick={() => setRules((rs) => [...rs, { type: 'silence', streamId: null, params: { thresholdDb: -60, seconds: 10 }, enabled: true }])}>
            <Plus size={14} aria-hidden /> Add rule
          </button>
        </div>
      </section>
    </div>
  );
}
